import { Injectable } from '@angular/core';
import {Resultado} from '../interface/result.inteface';
import {AnswerResponseDto, SurveySubmissionSummaryDto} from '../interface/survey-responses.interface';

interface ClientSelection {
  clientId: string;
  clientName: string;
}

interface CampaignSelection {
  campaignId: string;
  campaignName: string;
}

@Injectable({ providedIn: 'root' })
export class ResultStateService {
  private _cuestionario: Resultado | null = null;
  private _surveyAnswers: AnswerResponseDto[] = [];
  private _surveyAnswersByGuide: Map<string, AnswerResponseDto[]> = new Map();
  private _currentPage: number = 0;
  private _pageSize: number = 10;
  private _clientSelection: ClientSelection | null = null;
  private _campaignSelection: CampaignSelection | null = null;
  private _surveySubmissions: SurveySubmissionSummaryDto[] = [];
  private _filteredResults: Resultado[] = [];
  private _totalElements: number = 0;
  private _dataLoaded: boolean = false;

  setCuestionario(cuestionario: Resultado): void {
    this._cuestionario = cuestionario;
  }

  getCuestionario(): Resultado | null {
    return this._cuestionario;
  }

  clearCuestionario(): void {
    this._cuestionario = null;
  }

  setSurveyAnswers(answers: AnswerResponseDto[]): void {
    this._surveyAnswers = answers;
  }

  getSurveyAnswers(): AnswerResponseDto[] {
    return this._surveyAnswers;
  }

  clearSurveyAnswers(): void {
    this._surveyAnswers = [];
  }

  setSurveyAnswersForGuide(answers: AnswerResponseDto[], token: string, guideId: string): void {
    const key = `${token}-${guideId}`;
    this._surveyAnswersByGuide.set(key, answers);
  }

  getSurveyAnswersForGuide(token: string, guideId: string): AnswerResponseDto[] {
    const key = `${token}-${guideId}`;
    return this._surveyAnswersByGuide.get(key) || [];
  }

  clearSurveyAnswersForGuide(token: string, guideId: string): void {
    const key = `${token}-${guideId}`;
    this._surveyAnswersByGuide.delete(key);
  }

  clearAllSurveyAnswersForGuide(): void {
    this._surveyAnswersByGuide.clear();
  }

  setCurrentPage(page: number): void {
    this._currentPage = page;
  }

  getCurrentPage(): number {
    return this._currentPage;
  }

  clearCurrentPage(): void {
    this._currentPage = 0;
    console.log('ResultStateService: Current page cleared');
  }

  setPageSize(size: number): void {
    this._pageSize = size;
  }

  getPageSize(): number {
    return this._pageSize;
  }

  clearPageSize(): void {
    this._pageSize = 10;
  }

  // Client and Campaign selection persistence methods
  setClientSelection(clientId: string, clientName: string): void {
    this._clientSelection = { clientId, clientName };
  }

  getClientSelection(): ClientSelection | null {
    return this._clientSelection;
  }

  setCampaignSelection(campaignId: string, campaignName: string): void {
    this._campaignSelection = { campaignId, campaignName };
  }

  getCampaignSelection(): CampaignSelection | null {
    return this._campaignSelection;
  }

  clearSelections(): void {
    this._clientSelection = null;
    this._campaignSelection = null;
  }

  hasSelections(): boolean {
    return this._clientSelection !== null && this._campaignSelection !== null;
  }

  // Survey submissions caching methods
  setSurveySubmissions(submissions: SurveySubmissionSummaryDto[]): void {
    this._surveySubmissions = submissions;
  }

  getSurveySubmissions(): SurveySubmissionSummaryDto[] {
    return this._surveySubmissions;
  }

  setFilteredResults(results: Resultado[]): void {
    this._filteredResults = results;
  }

  getFilteredResults(): Resultado[] {
    return this._filteredResults;
  }

  setTotalElements(total: number): void {
    this._totalElements = total;
  }

  getTotalElements(): number {
    return this._totalElements;
  }

  setDataLoaded(loaded: boolean): void {
    this._dataLoaded = loaded;
  }

  getDataLoaded(): boolean {
    return this._dataLoaded;
  }

  clearCachedData(): void {
    this._surveySubmissions = [];
    this._filteredResults = [];
    this._totalElements = 0;
    this._dataLoaded = false;
  }
}
